import React, { useCallback, useState } from 'react';

import { Box, Button, MenuItem, Select, Stack, Typography } from '@mui/material';

import { CatalogRoom, RoomMatchResult } from '~/types/stopsell';

interface Props {
  match: RoomMatchResult;
  index: number;
  catalogRooms: Array<CatalogRoom>;
  onConfirm: (index: number) => void;
  onOverride: (index: number, catalogRoomId: string) => void;
}

export default function MatchActions({ match, index, catalogRooms, onConfirm, onOverride }: Props) {
  const [selectValue, setSelectValue] = useState(match.catalogRoomId ?? '');

  const handleSelectChange = useCallback(
    (value: string) => {
      setSelectValue(value);
      if (value) {
        onOverride(index, value);
      }
    },
    [index, onOverride],
  );

  return (
    <Box
      sx={{
        mt: 1,
        ml: 3.5,
        p: 1.5,
        bgcolor: match.catalogRoomId ? 'warning.50' : 'grey.50',
        border: '1px solid',
        borderColor: match.catalogRoomId ? 'warning.200' : 'divider',
        borderRadius: 1,
      }}
    >
      {match.catalogRoomId ? (
        <>
          <Typography variant="body2" color="text.secondary">
            Extracted room name has {Math.round(match.matchScore * 100)}% similarity with{' '}
            <strong>{match.catalogRoomName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            Confirm the match is correct, or select the correct room.
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Select
              size="small"
              displayEmpty
              value={selectValue}
              onChange={(e) => handleSelectChange(e.target.value)}
              sx={{ fontSize: 13, minWidth: 200 }}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="" disabled>
                Map to another room...
              </MenuItem>
              {catalogRooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
            <Button size="small" variant="contained" onClick={() => onConfirm(index)}>
              Confirm Match
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No match found. Select the correct room.
          </Typography>
          <Select
            size="small"
            displayEmpty
            value={selectValue}
            onChange={(e) => handleSelectChange(e.target.value)}
            sx={{ fontSize: 13, minWidth: 200 }}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}
          >
            <MenuItem value="" disabled>
              Select a room...
            </MenuItem>
            {catalogRooms.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
    </Box>
  );
}
